import { useState } from "react";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { AppHeader } from "@/components/app/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DEFAULT_THEME,
  FIELD_OPTION_COUNT,
  FIELD_SLOT_KEYS,
  STATUS_THEME_KEYS,
  getFieldSlotOptionKey,
  useColorThemeAdmin,
  type ThemeStyle,
} from "@/hooks/use-color-theme";

export const Route = createFileRoute("/_app/settings_/color-theme")({
  component: ColorThemePage,
});

const HEX_RE = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/;
const isHex = (v: string) => HEX_RE.test(v.trim());

export function ColorThemePage() {
  const { role, user } = useAuth();
  const { query, draft, setDraft } = useColorThemeAdmin();
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  if (role && role !== "admin") {
    toast.error("Only Admin can edit color theme.");
    return <Navigate to="/settings" />;
  }

  const updateField = (key: string, patch: Partial<ThemeStyle>) => {
    setDraft((d) => ({ ...d, [key]: { ...d[key], ...patch } }));
  };

  const validateDraft = () => {
    for (const fieldSlot of FIELD_SLOT_KEYS) {
      for (let option = 1; option <= FIELD_OPTION_COUNT; option++) {
        const key = getFieldSlotOptionKey(fieldSlot - 1, option - 1);
        const value = draft[key] ?? DEFAULT_THEME[key];
        if (!isHex(value.bg_color) || !isHex(value.text_color)) {
          toast.error(`Invalid hex color in Field Slot ${fieldSlot}, Option ${option}`);
          return false;
        }
        if (value.bg_color.toLowerCase() === value.text_color.toLowerCase()) {
          toast.error(`Field Slot ${fieldSlot}, Option ${option}: text and background must differ`);
          return false;
        }
      }
    }
    return true;
  };

  const buildRows = (source: Record<string, ThemeStyle>) => {
    const rows = [];
    for (const fieldSlot of FIELD_SLOT_KEYS) {
      for (let option = 1; option <= FIELD_OPTION_COUNT; option++) {
        const key = getFieldSlotOptionKey(fieldSlot - 1, option - 1);
        const def = DEFAULT_THEME[key];
        const value = source[key] ?? def;
        rows.push({
          theme_key: key,
          label: `Option Color ${option}`,
          group_name: `Field Slot ${fieldSlot} Colors`,
          bg_color: value.bg_color,
          text_color: value.text_color,
          border_color: value.border_color,
          usage_type: "pill",
          is_system: true,
          is_active: true,
          updated_by: user?.id ?? null,
        });
      }
    }
    return rows;
  };

  const handleSave = async () => {
    if (!validateDraft()) return;
    setSaving(true);
    const { error } = await (supabase.from("ui_color_theme" as never) as any).upsert(
      buildRows(draft),
      { onConflict: "theme_key" },
    );
    setSaving(false);
    if (error) toast.error("Color theme failed to save");
    else toast.success("Color theme saved");
    query.refetch();
  };

  const handleReset = async () => {
    setResetOpen(false);
    setSaving(true);
    const defaults: Record<string, ThemeStyle> = {};
    for (const fieldSlot of FIELD_SLOT_KEYS) {
      for (let option = 1; option <= FIELD_OPTION_COUNT; option++) {
        const key = getFieldSlotOptionKey(fieldSlot - 1, option - 1);
        const def = DEFAULT_THEME[key];
        defaults[key] = { bg_color: def.bg_color, text_color: def.text_color, border_color: def.border_color };
      }
    }
    const { error } = await (supabase.from("ui_color_theme" as never) as any).upsert(
      buildRows(defaults),
      { onConflict: "theme_key" },
    );
    setSaving(false);
    if (error) toast.error("Defaults failed to restore");
    else toast.success("Field slot colors reset");
    query.refetch();
  };

  return (
    <>
      <AppHeader subtitle="Set generic colors by field position and option position." />
      <div className="flex items-center justify-between px-[22px] pt-3">
        <h1 className="text-[18px] font-bold">Color Theme</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setResetOpen(true)} disabled={saving}>
            Reset to Default
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || query.isLoading}
            style={{ backgroundColor: "#7C3AED", color: "#FFFFFF" }}
            className="hover:opacity-90"
          >
            {saving ? "Saving…" : "Save Theme"}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 px-[22px] py-4 lg:grid-cols-[1fr_360px]">
        <div className="space-y-4">
          {FIELD_SLOT_KEYS.map((fieldSlot) => (
            <FieldSlotCard
              key={fieldSlot}
              fieldSlot={fieldSlot}
              draft={draft}
              onChange={updateField}
            />
          ))}
        </div>

        <div className="sticky top-4 h-fit space-y-4 rounded-lg border border-border bg-card p-4 shadow-sm">
          <h2 className="text-[14px] font-bold">Fixed Status Colors</h2>
          <p className="text-[12px] text-muted-foreground">
            Status values keep fixed meaning colors and do not use field slots.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {STATUS_THEME_KEYS.map((key) => (
              <PreviewPill
                key={key}
                label={DEFAULT_THEME[key].label.split(" /")[0]}
                bg={DEFAULT_THEME[key].bg_color}
                fg={DEFAULT_THEME[key].text_color}
              />
            ))}
          </div>
          <div className="border-t border-border pt-4">
            <h3 className="text-[12px] font-bold">Generic Rule</h3>
            <p className="mt-1 text-[12px] leading-5 text-muted-foreground">
              First non-status pill field uses Field Slot 1, second uses Field Slot 2, third uses Field Slot 3, fourth uses Field Slot 4. Inside each field, option order picks Color 1–15.
            </p>
          </div>
        </div>
      </div>

      <ResetDialog open={resetOpen} onOpenChange={setResetOpen} onConfirm={handleReset} />
    </>
  );
}

function FieldSlotCard({
  fieldSlot,
  draft,
  onChange,
}: {
  fieldSlot: number;
  draft: Record<string, ThemeStyle>;
  onChange: (key: string, patch: Partial<ThemeStyle>) => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <h2 className="text-[14px] font-bold">Field Slot {fieldSlot}</h2>
      <p className="mt-1 text-[12px] text-muted-foreground">
        Option Colors 1 to 15 for the {ordinal(fieldSlot)} non-status dropdown or pill field.
      </p>
      <div className="mt-4 grid gap-2 xl:grid-cols-2">
        {Array.from({ length: FIELD_OPTION_COUNT }, (_, i) => {
          const option = i + 1;
          const key = getFieldSlotOptionKey(fieldSlot - 1, i);
          const def = DEFAULT_THEME[key];
          const value = draft[key] ?? {
            bg_color: def.bg_color,
            text_color: def.text_color,
            border_color: null,
          };
          return (
            <OptionColorRow
              key={key}
              label={`Option ${option}`}
              bg={value.bg_color}
              fg={value.text_color}
              onChange={(patch) => onChange(key, patch)}
            />
          );
        })}
      </div>
    </div>
  );
}

function OptionColorRow({
  label,
  bg,
  fg,
  onChange,
}: {
  label: string;
  bg: string;
  fg: string;
  onChange: (patch: Partial<ThemeStyle>) => void;
}) {
  const bgValid = isHex(bg);
  const fgValid = isHex(fg);
  return (
    <div className="grid grid-cols-[84px_auto_auto_92px] items-center gap-2 rounded-md border border-border/60 px-3 py-2">
      <div className="text-[12px] font-semibold">{label}</div>
      <ColorField label="BG" value={bg} valid={bgValid} onChange={(v) => onChange({ bg_color: v })} />
      <ColorField label="Text" value={fg} valid={fgValid} onChange={(v) => onChange({ text_color: v })} />
      <PreviewPill label={label} bg={bgValid ? bg : "#F3F4F6"} fg={fgValid ? fg : "#6B7280"} />
    </div>
  );
}

function ColorField({
  label,
  value,
  valid,
  onChange,
}: {
  label: string;
  value: string;
  valid: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <input
        type="color"
        value={isHex(value) ? value : "#000000"}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        className="h-7 w-7 cursor-pointer rounded border border-border bg-transparent"
        title={label}
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`h-7 w-[92px] text-[11px] font-mono ${valid ? "" : "border-destructive"}`}
        spellCheck={false}
      />
    </div>
  );
}

function PreviewPill({ label, bg, fg }: { label: string; bg: string; fg: string }) {
  return (
    <span
      className="inline-flex min-w-0 justify-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
      style={{ backgroundColor: bg, color: fg }}
    >
      {label}
    </span>
  );
}

function ResetDialog({
  open,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset Field Slot Colors?</DialogTitle>
          <DialogDescription>
            This restores the default colors for all 4 field slots and their 15 option colors.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onConfirm}>Reset</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ordinal(n: number) {
  if (n === 1) return "first";
  if (n === 2) return "second";
  if (n === 3) return "third";
  return "fourth";
}
